import React, { useEffect, useContext, useState } from "react";
import { usePlaidLink, PlaidLinkOnSuccessMetadata } from "react-plaid-link";
import { Form } from "react-bootstrap";
import Context from "../Context";
import AddButtonWithArrow from "../commonCom/AddButtonWithArrow";
import CommonConfirmation from "../../common/CommonConfirmation";
import { PaymentService, PaymentMethodTypesEnum } from "@propertelligent/client-api";
import apiHelper from "../../../utils/apiHelper";
import { GetImages } from "../../../utils/GetImages";

// Backend PaymentMethodVM.AccountName is [Required][MaxLength(30)] -- keep the client in sync
// so the naming prompt can never submit a value the API would reject.
export const ACCOUNT_NAME_MAX_LENGTH = 30;

interface PendingPlaidLink {
  publicToken: string;
  metadata: PlaidLinkOnSuccessMetadata;
  defaultAccountName: string;
}

const Link = () => {
  const { linkToken, isPaymentInitiation, dispatch, plaidEntityConfig, plaidButtonLabel, plaidReconnectPaymentMethod } =
    useContext(Context);

  // Holds the successful Plaid Link result while the user confirms/edits the account name
  // before it is actually persisted -- lets every portal (Organization, Platform, My Settings,
  // Tenant, Contractor) reuse the same rename step since they all share this component.
  const [pendingLink, setPendingLink] = useState<PendingPlaidLink | null>(null);
  const [accountNameInput, setAccountNameInput] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const onSuccess = React.useCallback(
    (public_token: string, metadata: PlaidLinkOnSuccessMetadata) => {
      const linkedAccount = metadata.accounts?.[0];
      const institutionName = metadata.institution?.name ?? null;
      const defaultAccountName = (linkedAccount?.name || institutionName || "Plaid Account").slice(
        0,
        ACCOUNT_NAME_MAX_LENGTH
      );

      const isReconnect = !!plaidReconnectPaymentMethod;

      if (isPaymentInitiation) {
        dispatch({ type: "SET_STATE", state: { isItemAccess: false, linkSuccess: true } });
        return;
      }

      if (!plaidEntityConfig && !isReconnect) {
        // First-time-experience quickstart flow does not persist a payment method here at all
        // (it's picked up later in that flow), so there's nothing to name yet.
        dispatch({
          type: "SET_STATE",
          state: {
            isItemAccess: true,
            plaidAccountDetails: {
              accountName: defaultAccountName,
              accountNumberMask: linkedAccount?.mask ?? null,
              accountId: linkedAccount?.id ?? null,
              itemId: metadata.institution?.institution_id ?? null,
              institutionName,
              publicAccessToken: public_token,
            },
            linkSuccess: true,
          },
        });
        return;
      }

      // Defer the actual save until the user confirms (or edits) the account name.
      setAccountNameInput(defaultAccountName);
      setPendingLink({ publicToken: public_token, metadata, defaultAccountName });
    },
    [dispatch, isPaymentInitiation, plaidEntityConfig, plaidReconnectPaymentMethod]
  );

  const closeNamingPrompt = () => {
    setPendingLink(null);
    setAccountNameInput("");
  };

  const confirmAccountName = async () => {
    if (!pendingLink || isSaving) return;

    const { publicToken, metadata } = pendingLink;
    const linkedAccount = metadata.accounts?.[0];
    const institutionName = metadata.institution?.name ?? null;
    const accountName = accountNameInput.trim().slice(0, ACCOUNT_NAME_MAX_LENGTH) || pendingLink.defaultAccountName;
    const isReconnect = !!plaidReconnectPaymentMethod;
    // Connecting the org's signup-seeded default placeholder (type Default) is a first connection, not a re-link.
    const isFirstPlaidConnection =
      isReconnect && plaidReconnectPaymentMethod!.paymentMethodTypeId === PaymentMethodTypesEnum.Default;
    const toastCopy = isFirstPlaidConnection
      ? {
          title: "Plaid Account Connected",
          successMessage: "Plaid account connected successfully",
          failureMessage: "Unable to connect Plaid account",
        }
      : isReconnect
        ? {
            title: "Plaid Account Reconnected",
            successMessage: "Plaid account reconnected successfully",
            failureMessage: "Unable to reconnect Plaid account",
          }
        : {
            title: "Plaid Account Added",
            successMessage: "Plaid account linked successfully",
            failureMessage: "Unable to add Plaid account",
          };

    const plaidAccountDetails = {
      accountName,
      accountNumberMask: linkedAccount?.mask ?? null,
      accountId: linkedAccount?.id ?? null,
      itemId: metadata.institution?.institution_id ?? null,
      institutionName,
      publicAccessToken: publicToken,
    };

    setIsSaving(true);
    try {
      const plaidPayload = {
        accountName,
        paymentMethodTypeId: PaymentMethodTypesEnum.Plaid,
        plaidPaymentMethod: {
          publicAccessToken: publicToken,
          accountId: linkedAccount?.id ?? null,
          accountNumberMask: linkedAccount?.mask ?? null,
          itemId: metadata.institution?.institution_id ?? null,
          institutionName,
        },
        achPaymentMethod: undefined,
        creditCardPaymentMethod: undefined,
      };

      const payload = isReconnect
        ? {
            ...plaidReconnectPaymentMethod,
            ...plaidPayload,
            entityTypeId: plaidReconnectPaymentMethod!.entityTypeId,
            entityId: plaidReconnectPaymentMethod!.entityId,
          }
        : {
            ...plaidPayload,
            entityTypeId: plaidEntityConfig!.entityTypeId,
            entityId: plaidEntityConfig!.entityId,
          };

      await apiHelper(
        isReconnect ? PaymentService.putApiPaymentMethod : PaymentService.postApiPaymentMethod,
        {
          ...toastCopy,
          showNotification: true,
          successIcon: (
            <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />
          ),
        },
        payload
      );

      dispatch({
        type: "SET_STATE",
        state: {
          itemId: plaidAccountDetails.itemId,
          accessToken: publicToken,
          isItemAccess: true,
          plaidAccountDetails,
          linkSuccess: true,
        },
      });
      closeNamingPrompt();
    } catch (error) {
      console.error("Error saving Plaid payment method:", error);
      dispatch({
        type: "SET_STATE",
        state: {
          isItemAccess: false,
          plaidAccountDetails: null,
          linkSuccess: true,
        },
      });
      closeNamingPrompt();
    } finally {
      setIsSaving(false);
    }
  };

  let isOauth = false;
  const config: Parameters<typeof usePlaidLink>[0] = {
    // Empty string is the context default while the token is in flight; Plaid Link
    // only initializes on a real token. Passing "" can leave `ready` stuck false.
    token: linkToken || null,
    onSuccess,
  };

  if (window.location.href.includes("?oauth_state_id=")) {
    // TODO: figure out how to delete this ts-ignore
    // @ts-ignore
    config.receivedRedirectUri = window.location.href;
    isOauth = true;
  }

  const { open, ready } = usePlaidLink(config);

  useEffect(() => {
    if (isOauth && ready) {
      open();
    }
  }, [ready, open, isOauth]);

  return (
    <>
      <AddButtonWithArrow
        buttonname={plaidButtonLabel || "Connect Now"}
        onClick={(e) => {
          e.preventDefault();
          open();
        }}
        disabled={!ready}
        loading={linkToken !== null && !ready}
      />
      <CommonConfirmation
        show={!!pendingLink}
        heading="Name Your Payment Account"
        subHeading="This name shows up in payment account lists and dropdowns. You can change it later by editing the account."
        confirmLabel={isSaving ? "Saving..." : "Save"}
        cancelLabel="Cancel"
        onClose={isSaving ? undefined : closeNamingPrompt}
        onConfirm={accountNameInput.trim().length > 0 && !isSaving ? confirmAccountName : undefined}
        Element={
          <Form.Control
            type="text"
            value={accountNameInput}
            maxLength={ACCOUNT_NAME_MAX_LENGTH}
            placeholder="Account name"
            autoFocus
            disabled={isSaving}
            onChange={(e) => setAccountNameInput(e.target.value)}
          />
        }
      />
    </>
  );
};

Link.displayName = "Link";

export default Link;
